import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { Separator } from '../../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Alert,
  AlertDescription,
} from '../../../components/ui/alert';
import RichTextEditor, { RichTextDisplay } from '../../../components/ui/rich-text-editor';
import { CategoryAccordionSelector, PERSONAL_DATA_CATEGORIES, SENSITIVE_DATA_CATEGORIES } from '../../../components/ui/category-accordion-selector';
import { FormStepper, StepperProgress } from '../../../components/ui/form-stepper';
import { Textarea } from '../../../components/ui/textarea';
import { DepartmentCombo } from '../../../components/ui/department-combo';
import { ropaAPI, entitiesAPI, departmentsAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  ArrowLeft, Save, Building2,
  FileText, Users, Shield, Database, Share2, Server,
  Lock, AlertCircle, ClipboardCheck, Check, Clock,
  ChevronLeft, ChevronRight, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import { CARIBBEAN_ADEQUACY_COUNTRIES } from '../../../constants/compliance';

// ==================== CONSTANTS ====================

const DATA_SUBJECTS_OPTIONS = [
  'Employees', 'Customers', 'Contractors', 'Suppliers', 'Website Visitors',
  'Job Applicants', 'Members', 'Patients', 'Students', 'Clients',
  'Beneficiaries', 'Minors/Children', 'Other'
];

// Map coded values from the intake form to the display strings used by the edit form
const INTAKE_DATA_SUBJECT_MAP = {
  employees_staff: 'Employees',
  customers_clients: 'Customers',
  job_applicants: 'Job Applicants',
  contractors_vendors: 'Contractors',
  members_beneficiaries: 'Members',
  website_visitors: 'Website Visitors',
  students: 'Students',
  minors_children: 'Minors/Children',
  other: 'Other',
};

const normaliseDataSubjects = (values) =>
  (values || []).map(v => INTAKE_DATA_SUBJECT_MAP[v] || v);

// Merge data_collection_sources (new intake field) into data_collection_source (edit form field).
// Also maps legacy 'other' value to 'external'.
const normaliseDataCollectionSource = (record) => {
  const legacy = (record.data_collection_source || []).map(v => v === 'other' ? 'external' : v);
  const intake = record.data_collection_sources || [];
  const merged = Array.from(new Set([...legacy, ...intake]));
  return merged.length > 0 ? merged : legacy;
};

const LEGAL_BASIS_OPTIONS = [
  { value: 'consent', label: 'Consent', description: 'The individual has given clear consent for you to process their personal data for a specific purpose.' },
  { value: 'contract', label: 'Contract', description: 'The processing is necessary for a contract you have with the individual, or because they have asked you to take specific steps before entering into a contract.' },
  { value: 'legal_obligation', label: 'Legal Obligation', description: 'The processing is necessary for you to comply with the law (not including contractual obligations).' },
  { value: 'vital_interests', label: 'Vital Interests', description: 'The processing is necessary to protect someone\'s life.' },
  { value: 'public_task', label: 'Public Task', description: 'The processing is necessary for you to perform a task in the public interest or for your official functions, and the task or function has a clear basis in law.' },
  { value: 'legitimate_interests', label: 'Legitimate Interests', description: 'The processing is necessary for your legitimate interests or the legitimate interests of a third party, unless there is a good reason to protect the individual\'s personal data which overrides those legitimate interests.' }
];

// Legal basis prompt messages (shown when each basis is selected)
const LEGAL_BASIS_PROMPTS = {
  consent: 'Have you obtained a clear, specific, and freely given consent from the individual for this processing? Consent must be withdrawable at any time.',
  contract: 'Is this processing directly necessary to perform a contract with the individual, or to take steps they requested before entering a contract?',
  legal_obligation: 'Does a specific law or regulation require you to carry out this processing? Identify the legislation in the justification field below.',
  vital_interests: 'Is this processing necessary to protect someone\'s life where consent cannot reasonably be obtained? This basis should be used rarely.',
  public_task: 'Is this processing necessary for a task carried out in the public interest, or in the exercise of official authority? Public bodies should primarily use this basis.',
  legitimate_interests: 'Have you conducted a Legitimate Interests Assessment (LIA)? This basis requires that the organisation\'s interest outweighs the individual\'s rights. Do not use this as a default.'
};

// Sensitive data legal basis prompts (shown when each basis is selected)
const SENSITIVE_DATA_BASIS_PROMPTS = {
  explicit_consent: 'Ensure you have explicit, informed, written consent from the individual specifically for processing sensitive data. This must be freely given and easily withdrawable.',
  employment_law: 'Processing must be necessary under employment law or collective bargaining agreements. Ensure the legal requirement is clearly documented.',
  vital_interests: 'Processing may be necessary to protect vital interests (health, life, safety) where consent cannot be obtained. This is used rarely and requires careful justification.',
  health_purposes: 'Processing health data is permitted for direct healthcare purposes, public health interests, or occupational health. Ensure the purpose is clearly aligned with healthcare or health research.',
  public_health: 'Processing may be necessary for public health reasons, disease control, or health emergencies. Document the public health justification clearly.',
  legal_claims: 'Processing may be necessary to establish, exercise, or defend legal claims. This includes litigation, insurance claims, or regulatory proceedings.'
};

// Transfer Risk Configuration - Countries with data transfer risk assessment
const TRANSFER_RISK_CONFIG = {
  noWarning: {
    label: 'No Warning',
    countries: ['Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic', 'Denmark', 'Estonia', 'Finland', 'France', 'Germany', 'Greece', 'Hungary', 'Iceland', 'Ireland', 'Italy', 'Latvia', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Malta', 'Netherlands', 'Norway', 'Poland', 'Portugal', 'Romania', 'Slovakia', 'Slovenia', 'Spain', 'Sweden', 'Switzerland', 'United Kingdom', ...CARIBBEAN_ADEQUACY_COUNTRIES],
    color: 'text-slate-600 dark:text-slate-400',
    bgColor: 'bg-slate-50 dark:bg-slate-900/30',
    borderColor: 'border-slate-200 dark:border-slate-700'
  },
  advisory: {
    label: 'Advisory Flag',
    countries: ['United States', 'Canada', 'Singapore', 'Australia', 'India', 'Japan', 'South Korea', 'Mexico', 'Brazil'],
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-950/30',
    borderColor: 'border-amber-200 dark:border-amber-700',
    message: 'This country does not have a formal adequacy determination. Ensure that appropriate transfer safeguards (such as Standard Contractual Clauses or a Data Sharing Agreement) are documented in the Transfer Safeguards field below.'
  },
  highRisk: {
    label: 'High-Risk Flag',
    countries: ['Afghanistan', 'Belarus', 'China', 'Cuba', 'Iran', 'North Korea', 'Russia', 'Syria', 'Yemen'],
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-950/30',
    borderColor: 'border-red-200 dark:border-red-700',
    message: 'Transfers to this country carry elevated risk. You may be required to conduct a Transfer Impact Assessment before processing can lawfully proceed. Consult your DPO.'
  }
};

// Helper function to get all countries and their risk levels
const getAllCountries = () => {
  const countries = new Set();
  Object.values(TRANSFER_RISK_CONFIG).forEach(config => {
    config.countries.forEach(c => countries.add(c));
  });
  return Array.from(countries).sort();
};

// Helper function to get risk level for a country
const getCountryRiskLevel = (country) => {
  for (const [level, config] of Object.entries(TRANSFER_RISK_CONFIG)) {
    if (config.countries.includes(country)) {
      return level;
    }
  }
  return 'unknown';
};

const ELECTRONIC_SECURITY_MEASURES = [
  'Password Protection', 'Encryption', 'Access Controls/Role-Based Access', 'Firewalls',
  'Anti-Virus/Anti-Malware Software', 'Regular Backups', 'Multi-Factor Authentication (MFA)',
  'Data Masking', 'Audit Logs', 'VPN', 'Intrusion Detection System', 'Regular Security Updates', 'Other'
];

const PAPER_SECURITY_MEASURES = [
  'Locked Filing Cabinets', 'Secure Storage Room', 'Restricted Access Area', 'CCTV Surveillance',
  'Sign-In/Sign-Out Logs', 'Clean Desk Policy', 'Shredding/Secure Disposal',
  'Key Management System', 'Visitor Logs', 'Security Personnel', 'Other'
];

const STEPS = [
  { id: 1, key: 'basics', label: 'Basics', icon: Building2 },
  { id: 2, key: 'data-collected', label: 'Data Collected', icon: Database },
  { id: 3, key: 'legal-basis', label: 'Legal Basis', icon: Shield },
  { id: 4, key: 'data-sources', label: 'Data Sources & Retention', icon: Users },
  { id: 5, key: 'storage-sharing-security', label: 'Storage, Sharing & Security', icon: Lock },
];

// Helper function to get available statuses based on user role
const getAvailableStatuses = (userRole) => {
  const ALL_STATUSES = ['draft', 'pending_review', 'approved', 'active', 'archived'];

  // DPO / Super Admin: simplified path (skip review)
  if (['dpo', 'super_admin', 'org_owner'].includes(userRole)) {
    return ['draft', 'active', 'archived'];
  }

  // Client/Entity Admin: simplified path (skip review)
  if (['client_admin', 'entity_admin'].includes(userRole)) {
    return ['draft', 'active', 'archived'];
  }

  // Staff / Viewers: can only draft and submit for review
  if (['client_view', 'entity_view'].includes(userRole)) {
    return ['draft', 'pending_review'];
  }

  // Default: full workflow
  return ALL_STATUSES;
};

// Required fields per step for validation
const STEP_REQUIRED_FIELDS = {
  1: ['process_name', 'purpose', 'department'],
  2: ['data_subjects', 'personal_data_types'],
  3: ['legal_basis'],
  4: ['retention_period'],
  5: [] // Storage, Sharing & Security - no required fields
};

// Initial form state
const initialFormData = {
  entity_id: '',
  department: '',
  unit: '',
  process_name: '',
  purpose: '',
  record_types: ['digital'],
  data_subjects: [],
  personal_data_types: [],
  legal_basis: '',
  legal_basis_explanation: '',
  collects_sensitive_data: false,
  sensitive_data_types: [],
  sensitive_data_legal_basis: '',
  data_collection_source: ['directly'],
  other_source_details: '',
  retention_period: '',
  retention_reason: '',
  shares_internally: false,
  internal_sharing_areas: '',
  shares_externally: false,
  external_sharing_recipients: '',
  transfer_location: null,
  has_data_sharing_agreement: null,
  uses_systems: false,
  system_name: '',
  system_location: null,
  cloud_country: '',
  electronic_security_measures: [],
  keeps_paper_records: false,
  paper_storage_location: [],
  external_storage_company: '',
  external_storage_location: '',
  paper_security_measures: [],
  security_notes: '',
  status: 'draft'
};

// ==================== COMPONENTS ====================

// Multi-select checkbox component
const MultiSelectCheckbox = ({ options, selected, onChange, columns = 3 }) => {
  const toggleOption = (option) => {
    if (selected.includes(option)) {
      onChange(selected.filter(s => s !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  return (
    <div className={cn(
      "grid gap-2",
      columns === 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
    )}>
      {options.map(option => (
        <div key={option} className="flex items-center space-x-2">
          <Checkbox 
            id={`cb-${option.replace(/\s+/g, '-')}`}
            checked={selected.includes(option)}
            onCheckedChange={() => toggleOption(option)}
          />
          <label 
            htmlFor={`cb-${option.replace(/\s+/g, '-')}`}
            className="text-sm cursor-pointer select-none"
          >
            {option}
          </label>
        </div>
      ))}
    </div>
  );
};

// Yes/No Button Group
const YesNoButtons = ({ value, onChange, yesLabel = 'Yes', noLabel = 'No' }) => (
  <div className="flex gap-2">
    <Button
      type="button"
      variant={value === true ? "default" : "outline"}
      size="sm"
      onClick={() => onChange(true)}
    >
      {yesLabel}
    </Button>
    <Button
      type="button"
      variant={value === false ? "default" : "outline"}
      size="sm"
      onClick={() => onChange(false)}
    >
      {noLabel}
    </Button>
  </div>
);

// ==================== MAIN COMPONENT ====================

export default function ROPAFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isDPO, isClientAdmin, isEntityAdmin, user } = useAuth();
  const { theme } = useTheme();
  const isEditing = !!id;
  
  const [formData, setFormData] = useState(initialFormData);
  const [entities, setEntities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const [currentStep, setCurrentStep] = useState(1);
  const [recordId, setRecordId] = useState(id || null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  
  const lastSavedData = useRef(null);
  const autoSaveTimer = useRef(null);
  const recordIdRef = useRef(id || null);

  // Calculate progress percentage
  const calculateProgress = useCallback(() => {
    let totalFields = 0;
    let completedFields = 0;

    // Basics
    totalFields += 4;
    if (formData.entity_id || !isDPO) completedFields++;
    if (formData.department) completedFields++;
    if (formData.process_name) completedFields++;
    if (formData.purpose && formData.purpose.replace(/<[^>]*>/g, '').trim()) completedFields++;

    // Data Collected
    totalFields += 2;
    if (formData.data_subjects.length > 0) completedFields++;
    if (formData.personal_data_types.length > 0) completedFields++;

    // Legal Basis
    totalFields += 1;
    if (formData.legal_basis) completedFields++;

    // Data Sources
    totalFields += 2;
    if (formData.data_collection_source.length > 0) completedFields++;
    if (formData.retention_period) completedFields++;

    // Data Sharing (optional but tracked)
    totalFields += 2;
    if (formData.shares_internally !== null) completedFields++;
    if (formData.shares_externally !== null) completedFields++;

    // Systems & Security
    totalFields += 2;
    if (formData.uses_systems !== null) completedFields++;
    if (formData.electronic_security_measures.length > 0 || formData.keeps_paper_records !== null) completedFields++;

    return Math.round((completedFields / totalFields) * 100);
  }, [formData, isDPO]);

  const progressPercentage = calculateProgress();

  // Validate current step
  const validateStep = useCallback((stepId) => {
    const errors = [];
    const requiredFields = STEP_REQUIRED_FIELDS[stepId] || [];

    requiredFields.forEach(field => {
      if (field === 'process_name' && !formData.process_name.trim()) {
        errors.push('Processing Activity Name is required');
      }
      if (field === 'purpose') {
        const plainText = (formData.purpose || '').replace(/<[^>]*>/g, '').trim();
        if (!plainText) {
          errors.push('Purpose of Processing is required');
        }
      }
      if (field === 'department' && !formData.department.trim()) {
        errors.push('Department is required');
      }
      if (field === 'data_subjects' && formData.data_subjects.length === 0) {
        errors.push('Please select at least one Data Subject');
      }
      if (field === 'personal_data_types' && formData.personal_data_types.length === 0) {
        errors.push('Please select at least one Personal Data Type');
      }
      if (field === 'legal_basis' && !formData.legal_basis) {
        errors.push('Please select a Legal Basis');
      }
      if (field === 'retention_period' && !formData.retention_period.trim()) {
        errors.push('Retention Period is required');
      }
    });

    // DPO and Client Admin must select entity
    if (stepId === 1 && (isDPO || isClientAdmin) && !formData.entity_id) {
      errors.push('Please select an Entity');
    }

    return errors;
  }, [formData, isDPO, isClientAdmin]);

  // Get completed steps
  const getCompletedSteps = useCallback(() => {
    let completed = 0;
    
    for (let i = 1; i < STEPS.length; i++) {
      const errors = validateStep(i);
      if (errors.length === 0) {
        completed = i;
      } else {
        break;
      }
    }
    
    return completed;
  }, [validateStep]);

  const completedSteps = getCompletedSteps();

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (isDPO) {
          const entitiesRes = await entitiesAPI.getAll();
          setEntities(entitiesRes.data);
        } else if (isClientAdmin) {
          // Client Admin can pick any entity under their client
          const entitiesRes = await entitiesAPI.getAll(user?.client_id);
          setEntities(entitiesRes.data || []);
        } else if (isEntityAdmin && !isEditing) {
          // Entity Admin: entity is fixed — pre-fill it so the department field is enabled
          const entityId = user?.entity_id || '';
          if (entityId) {
            setFormData(prev => ({ ...prev, entity_id: entityId }));
          }
        }

        if (isEditing) {
          const recordRes = await ropaAPI.getOne(id);
          const record = recordRes.data;
          setFormData({
            ...initialFormData,
            ...record,
            data_subjects: normaliseDataSubjects(record.data_subjects),
            data_collection_source: normaliseDataCollectionSource(record),
            unit: record.unit || '',
            legal_basis_explanation: record.legal_basis_explanation || '',
            other_source_details: record.other_source_details || '',
            retention_reason: record.retention_reason || '',
            internal_sharing_areas: record.internal_sharing_areas || '',
            external_sharing_recipients: record.external_sharing_recipients || '',
            transfer_location: record.transfer_location || null,
            system_name: record.system_name || '',
            system_location: record.system_location || null,
            cloud_country: record.cloud_country || '',
            external_storage_company: record.external_storage_company || '',
            external_storage_location: record.external_storage_location || '',
            sensitive_data_legal_basis: record.sensitive_data_legal_basis || '',
          });
          lastSavedData.current = JSON.stringify(record);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEditing, isDPO, isClientAdmin, isEntityAdmin]);

  // Fetch departments when entity changes
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!formData.entity_id) {
        setDepartments([]);
        return;
      }

      try {
        const response = await departmentsAPI.getByEntity(formData.entity_id);
        setDepartments(response.data);
      } catch (error) {
        console.error('Failed to fetch departments:', error);
        setDepartments([]);
      }
    };

    fetchDepartments();
  }, [formData.entity_id]);

  // Auto-save logic
  const performAutoSave = useCallback(async () => {
    if (!formData.process_name) return;
    if (!formData.entity_id && isDPO) return;
    
    const currentData = JSON.stringify(formData);
    if (currentData === lastSavedData.current) return;

    try {
      setAutoSaveStatus('saving');
      
      const submitData = {
        ...formData,
        entity_id: formData.entity_id || user?.organization_id,
        system_location: formData.system_location || null
      };
      
      if (recordIdRef.current) {
        await ropaAPI.update(recordIdRef.current, submitData);
      } else {
        const res = await ropaAPI.create(submitData);
        setRecordId(res.data.id);
        recordIdRef.current = res.data.id;
        window.history.replaceState(null, '', `/compliance/ropa/edit/${res.data.id}`);
      }
      
      lastSavedData.current = currentData;
      setAutoSaveStatus('saved');
      
      setTimeout(() => setAutoSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Auto-save failed:', error);
      setAutoSaveStatus('idle');
    }
  }, [formData, isDPO, user]);

  // Debounced auto-save
  useEffect(() => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    
    autoSaveTimer.current = setTimeout(() => {
      performAutoSave();
    }, 2000);

    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [formData, performAutoSave]);

  // Handle field change
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setShowValidationAlert(false);
  };

  // Handle multi-select toggle
  const toggleMultiSelect = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      }
      return { ...prev, [field]: [...current, value] };
    });
    setShowValidationAlert(false);
  };

  // Navigate to next step with validation
  const goToNextStep = () => {
    const errors = validateStep(currentStep);

    if (errors.length > 0) {
      // Ensure all errors are strings
      const errorMessages = errors.map(err => typeof err === 'string' ? err : String(err));
      setValidationErrors(errorMessages);
      setShowValidationAlert(true);
      return;
    }
    
    setShowValidationAlert(false);
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Navigate to previous step
  const goToPrevStep = () => {
    setShowValidationAlert(false);
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Final save
  const handleSave = async () => {
    // Validate all required steps
    const allErrors = [];
    STEPS.slice(0, -1).forEach(step => {
      const stepErrors = validateStep(step.id);
      if (stepErrors.length > 0) {
        allErrors.push({ step: step.label, errors: stepErrors });
      }
    });

    if (allErrors.length > 0) {
      const firstStepWithErrors = STEPS.find(s =>
        allErrors.some(e => e.step === s.label)
      );
      if (firstStepWithErrors) {
        setCurrentStep(firstStepWithErrors.id);
      }
      // Ensure all errors are strings
      const errorMessages = allErrors.flatMap(e =>
        e.errors.map(err => typeof err === 'string' ? err : String(err))
      );
      setValidationErrors(errorMessages);
      setShowValidationAlert(true);
      toast.error('Please complete all required fields before saving');
      return;
    }

    try {
      setSaving(true);

      const submitData = {
        ...formData,
        entity_id: formData.entity_id || user?.organization_id,
        system_location: formData.system_location || null,
        status: 'active'
      };

      if (recordIdRef.current) {
        await ropaAPI.update(recordIdRef.current, submitData);
        toast.success('Processing activity saved successfully');
      } else {
        const res = await ropaAPI.create(submitData);
        recordIdRef.current = res.data.id;
        setRecordId(res.data.id);
        toast.success('Processing activity created successfully');
      }
      
      navigate('/compliance/ropa');
    } catch (error) {
      console.error('Save failed:', error);
      const errorMessage = typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail
        : 'Failed to save';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Get legal basis label
  const getLegalBasisLabel = (value) => {
    const option = LEGAL_BASIS_OPTIONS.find(o => o.value === value);
    return option?.label || value;
  };

  // ==================== STEP RENDER FUNCTIONS ====================

  // Step 1: Basics
  const renderBasics = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            Organisation & Department
          </CardTitle>
          <CardDescription>
            Identify which part of your organisation this processing activity belongs to
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {(isDPO || isClientAdmin) && (
            <div className="space-y-2">
              <Label>Entity <span className="text-red-500">*</span></Label>
              <Select
                value={formData.entity_id || "select"}
                onValueChange={(val) => handleChange('entity_id', val === "select" ? "" : val)}
              >
                <SelectTrigger data-testid="entity-select">
                  <SelectValue placeholder="Select entity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="select">Select entity</SelectItem>
                  {entities.map(entity => (
                    <SelectItem key={entity.id} value={entity.id}>
                      {entity.entity_name} {entity.client_name ? `(${entity.client_name})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Department <span className="text-red-500">*</span></Label>
              <DepartmentCombo
                value={formData.department}
                onChange={(val) => handleChange('department', val)}
                entityId={formData.entity_id}
                departments={departments}
                placeholder="e.g., Human Resources, Finance"
                disabled={!formData.entity_id}
              />
            </div>
            <div className="space-y-2">
              <Label>Unit (if applicable)</Label>
              <Input
                value={formData.unit}
                onChange={(e) => handleChange('unit', e.target.value)}
                placeholder="e.g., Payroll, Recruitment"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Processing Activity Details
          </CardTitle>
          <CardDescription>
            Describe the processing activity and its purpose
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Name of Processing Activity <span className="text-red-500">*</span></Label>
            <Input
              value={formData.process_name}
              onChange={(e) => handleChange('process_name', e.target.value)}
              placeholder="e.g., Employee Payroll Processing"
            />
          </div>
          <div className="space-y-2">
            <Label>Purpose of Processing <span className="text-red-500">*</span></Label>
            <RichTextEditor
              value={formData.purpose}
              onChange={(val) => handleChange('purpose', val)}
              placeholder="Describe why this data is being processed. You can use formatting like bold, bullets, etc."
              minHeight="150px"
            />
          </div>
          <div className="space-y-2">
            <Label>Types of Records</Label>
            <div className="flex flex-wrap gap-4">
              {['paper', 'digital', 'both'].map(type => (
                <div key={type} className="flex items-center space-x-2">
                  <Checkbox
                    id={`record-${type}`}
                    checked={formData.record_types.includes(type)}
                    onCheckedChange={() => toggleMultiSelect('record_types', type)}
                  />
                  <label htmlFor={`record-${type}`} className="text-sm capitalize cursor-pointer">
                    {type}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Step 2: Data Collected
  const renderDataCollected = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Data Subjects
          </CardTitle>
          <CardDescription>
            Identify whose personal data is being collected
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Label className="mb-3 block">
            Whose personal data is being collected? <span className="text-red-500">*</span>
            <span className="text-muted-foreground font-normal ml-1">(select all that apply)</span>
          </Label>
          <MultiSelectCheckbox
            options={DATA_SUBJECTS_OPTIONS}
            selected={formData.data_subjects}
            onChange={(val) => handleChange('data_subjects', val)}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-primary" />
            Types of Personal Data
          </CardTitle>
          <CardDescription>
            Select the categories and specific types of personal data being collected. Click on a category to expand and select items.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Label className="mb-3 block">
            Type of Personal Data being collected <span className="text-red-500">*</span>
            <span className="text-muted-foreground font-normal ml-1">(click category to expand)</span>
          </Label>
          {formData.personal_data_types.length > 0 && (
            <div className="mb-4 p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground mb-2">Selected: {formData.personal_data_types.length} items</p>
              <div className="flex flex-wrap gap-1">
                {formData.personal_data_types.slice(0, 10).map((item, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">{item}</Badge>
                ))}
                {formData.personal_data_types.length > 10 && (
                  <Badge variant="outline" className="text-xs">+{formData.personal_data_types.length - 10} more</Badge>
                )}
              </div>
            </div>
          )}
          <CategoryAccordionSelector
            categories={PERSONAL_DATA_CATEGORIES}
            selectedItems={formData.personal_data_types}
            onChange={(val) => handleChange('personal_data_types', val)}
          />
        </CardContent>
      </Card>
    </div>
  );

  // Step 3: Legal Basis
  const renderLegalBasis = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Legal Basis for Processing
          </CardTitle>
          <CardDescription>
            Select the lawful basis for collecting and processing this personal data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Label>Legal Basis <span className="text-red-500">*</span></Label>
          <div className="grid gap-3">
            {LEGAL_BASIS_OPTIONS.map(option => (
              <div key={option.value}>
                <div
                  className={cn(
                    "p-4 border rounded-lg cursor-pointer transition-colors",
                    formData.legal_basis === option.value
                      ? "border-primary bg-primary/5"
                      : "hover:border-muted-foreground/50"
                  )}
                  onClick={() => handleChange('legal_basis', option.value)}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox checked={formData.legal_basis === option.value} />
                    <div>
                      <p className="font-medium">{option.label}</p>
                      <p className="text-sm text-muted-foreground mt-1">{option.description}</p>
                    </div>
                  </div>
                </div>

                {/* Show prompt below when this basis is selected */}
                {formData.legal_basis === option.value && (
                  <div className={cn(
                    "mt-3 p-3 rounded-lg text-sm",
                    option.value === 'legitimate_interests'
                      ? "bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100"
                      : "bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-100"
                  )}>
                    {LEGAL_BASIS_PROMPTS[option.value]}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-primary" />
            Sensitive Data (Special Categories)
          </CardTitle>
          <CardDescription>
            Indicate if you collect any special category data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Context block explaining sensitive data */}
          <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
            <h4 className="font-semibold text-sm text-blue-900 dark:text-blue-100 mb-2">What is sensitive data?</h4>
            <p className="text-sm text-blue-800 dark:text-blue-200 leading-relaxed">
              Certain categories of personal data attract a higher level of legal protection — for example, health information, biometric data, racial or ethnic origin, and criminal records. If your process collects any of these, you are required to identify an additional legal basis. <strong>Be accurate here: under-declaring sensitive data is a common compliance gap.</strong>
            </p>
          </div>

          <div className="flex items-center justify-between">
            <Label>Is Sensitive Data Collected?</Label>
            <YesNoButtons
              value={formData.collects_sensitive_data}
              onChange={(val) => handleChange('collects_sensitive_data', val)}
            />
          </div>

          {formData.collects_sensitive_data && (
            <div className="space-y-4 pt-4 border-t">
              <div className="space-y-2">
                <Label className="mb-3 block">
                  Type of Sensitive Data Collected
                  <span className="text-muted-foreground font-normal ml-1">(click category to expand)</span>
                </Label>
                {formData.sensitive_data_types.length > 0 && (
                  <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <p className="text-xs text-amber-800 dark:text-amber-200 mb-2">⚠️ Selected: {formData.sensitive_data_types.length} sensitive data types</p>
                    <div className="flex flex-wrap gap-1">
                      {formData.sensitive_data_types.slice(0, 8).map((item, i) => (
                        <Badge key={i} variant="warning" className="text-xs">{item}</Badge>
                      ))}
                      {formData.sensitive_data_types.length > 8 && (
                        <Badge variant="outline" className="text-xs">+{formData.sensitive_data_types.length - 8} more</Badge>
                      )}
                    </div>
                  </div>
                )}
                <CategoryAccordionSelector
                  categories={SENSITIVE_DATA_CATEGORIES}
                  selectedItems={formData.sensitive_data_types}
                  onChange={(val) => handleChange('sensitive_data_types', val)}
                />
              </div>
              <div className="space-y-2 pt-4 border-t">
                <Label>Legal Basis for Collecting Sensitive Data</Label>
                <Select
                  value={formData.sensitive_data_legal_basis || "select"}
                  onValueChange={(val) => handleChange('sensitive_data_legal_basis', val === "select" ? "" : val)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select legal basis" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="select">Select legal basis</SelectItem>
                    <SelectItem value="explicit_consent">Explicit Consent</SelectItem>
                    <SelectItem value="employment_law">Employment/Social Security Law</SelectItem>
                    <SelectItem value="vital_interests">Vital Interests</SelectItem>
                    <SelectItem value="health_purposes">Health Purposes</SelectItem>
                    <SelectItem value="public_health">Public Health</SelectItem>
                    <SelectItem value="legal_claims">Legal Claims</SelectItem>
                  </SelectContent>
                </Select>

                {/* Sensitive data legal basis guidance prompt */}
                {formData.sensitive_data_legal_basis && SENSITIVE_DATA_BASIS_PROMPTS[formData.sensitive_data_legal_basis] && (
                  <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-900 dark:text-blue-100">
                    {SENSITIVE_DATA_BASIS_PROMPTS[formData.sensitive_data_legal_basis]}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Step 4: Data Sources
  const renderDataSources = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Data Collection Source
          </CardTitle>
          <CardDescription>
            Indicate where the personal data is collected from
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Label>Who is personal data collected from?</Label>
          <div className="flex flex-col gap-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="source-directly"
                checked={formData.data_collection_source.includes('directly')}
                onCheckedChange={() => toggleMultiSelect('data_collection_source', 'directly')}
              />
              <label htmlFor="source-directly" className="text-sm cursor-pointer">
                Directly from the individual
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="source-internal"
                checked={formData.data_collection_source.includes('internal')}
                onCheckedChange={() => toggleMultiSelect('data_collection_source', 'internal')}
              />
              <label htmlFor="source-internal" className="text-sm cursor-pointer">
                From another department within the organisation
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="source-external"
                checked={formData.data_collection_source.includes('external')}
                onCheckedChange={() => toggleMultiSelect('data_collection_source', 'external')}
              />
              <label htmlFor="source-external" className="text-sm cursor-pointer">
                From an external source
              </label>
            </div>
          </div>

          {formData.data_collection_source.includes('external') && (
            <div className="space-y-2 pt-4 border-t">
              <Label>If from an external source, please specify who and the reason</Label>
              <RichTextEditor
                value={formData.other_source_details}
                onChange={(val) => handleChange('other_source_details', val)}
                placeholder="e.g., Government agency, credit bureau, recruitment agency..."
                minHeight="100px"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            Data Retention
          </CardTitle>
          <CardDescription>
            Specify how long personal data is kept
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>How Long is Personal Data Retained? <span className="text-red-500">*</span></Label>
              <Input
                value={formData.retention_period}
                onChange={(e) => handleChange('retention_period', e.target.value)}
                placeholder="e.g., 7 years"
              />
            </div>
            <div className="space-y-2">
              <Label>Reason for Retention Period</Label>
              <Input
                value={formData.retention_reason}
                onChange={(e) => handleChange('retention_reason', e.target.value)}
                placeholder="e.g., Legal requirement"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Step 5: Storage, Sharing & Security (merged from previous steps 5 & 6) with inline summary - see renderStorageSharingSecurityWithSummary below

  // Systems & Security (merged into Step 5)

  // Step 5: Storage, Sharing & Security (merged from previous steps 5 & 6) with inline summary
  const renderStorageSharingSecurityWithSummary = () => {
    return (
      <div className="space-y-6">
        {/* Data Sharing Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Share2 className="h-5 w-5 text-primary" />
              Data Sharing
            </CardTitle>
            <CardDescription>
              Indicate where personal data is shared
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Do you share data internally (within your organization)?</Label>
                <YesNoButtons
                  value={formData.shares_internally}
                  onChange={(val) => handleChange('shares_internally', val)}
                />
              </div>
              {formData.shares_internally && (
                <div className="pl-4 space-y-2 border-l-2 border-blue-200 dark:border-blue-800">
                  <Label>Internal Sharing Areas</Label>
                  <Textarea
                    value={formData.internal_sharing_areas || ''}
                    onChange={(e) => handleChange('internal_sharing_areas', e.target.value)}
                    placeholder="Describe which departments or teams have access to this data"
                    className="text-sm min-h-24"
                  />
                </div>
              )}
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Do you share data externally (with third parties)?</Label>
                <YesNoButtons
                  value={formData.shares_externally}
                  onChange={(val) => handleChange('shares_externally', val)}
                />
              </div>
              {formData.shares_externally && (
                <div className="pl-4 space-y-4 border-l-2 border-blue-200 dark:border-blue-800">
                  <div className="space-y-2">
                    <Label>External Sharing Recipients</Label>
                    <Textarea
                      value={formData.external_sharing_recipients || ''}
                      onChange={(e) => handleChange('external_sharing_recipients', e.target.value)}
                      placeholder="List the third parties, their location, and the purpose of sharing"
                      className="text-sm min-h-24"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Transfer Location</Label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { value: 'local', label: 'Local' },
                        { value: 'regional', label: 'Regional' },
                        { value: 'international', label: 'International' },
                        { value: 'regulator', label: 'Regulator' },
                      ].map(({ value, label }) => (
                        <Button
                          key={value}
                          type="button"
                          variant={formData.transfer_location === value ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleChange('transfer_location', value)}
                        >
                          {label}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Systems & Software Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-5 w-5 text-primary" />
              Systems & Software
            </CardTitle>
            <CardDescription>
              Identify any systems used for this processing activity
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Do you use any particular system or software?</Label>
              <YesNoButtons
                value={formData.uses_systems}
                onChange={(val) => handleChange('uses_systems', val)}
              />
            </div>

            {formData.uses_systems && (
              <div className="space-y-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label>Name of System or Software</Label>
                  <Input
                    value={formData.system_name}
                    onChange={(e) => handleChange('system_name', e.target.value)}
                    placeholder="e.g., SAP, Salesforce"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Where is it managed?</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={formData.system_location === 'internal' ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleChange('system_location', 'internal')}
                    >
                      Internal (On-Premises)
                    </Button>
                    <Button
                      type="button"
                      variant={formData.system_location === 'cloud' ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleChange('system_location', 'cloud')}
                    >
                      Cloud
                    </Button>
                  </div>
                </div>
                {formData.system_location === 'cloud' && (
                  <div className="space-y-3">
                    <Label>Which country is the data stored in?</Label>
                    <Select value={formData.cloud_country} onValueChange={(val) => handleChange('cloud_country', val)}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a country..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-48">
                        {getAllCountries().map(country => (
                          <SelectItem key={country} value={country}>
                            {country}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Transfer Risk Warning */}
                    {formData.cloud_country && (() => {
                      const riskLevel = getCountryRiskLevel(formData.cloud_country);
                      const config = TRANSFER_RISK_CONFIG[riskLevel];

                      if (riskLevel === 'unknown') {
                        return (
                          <div className={cn(
                            "p-3 rounded-lg border text-sm",
                            "bg-gray-50 dark:bg-gray-900/30 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300"
                          )}>
                            ⚠️ This country is not in the pre-configured list. Consult your DPO to assess transfer risks.
                          </div>
                        );
                      }

                      if (riskLevel === 'advisory') {
                        return (
                          <div className={cn(
                            "p-3 rounded-lg border text-sm",
                            config.bgColor,
                            config.borderColor,
                            config.color
                          )}>
                            <p className="font-medium mb-1">⚠️ {config.label}</p>
                            <p>{config.message}</p>
                          </div>
                        );
                      }

                      if (riskLevel === 'highRisk') {
                        return (
                          <div className={cn(
                            "p-3 rounded-lg border text-sm",
                            config.bgColor,
                            config.borderColor,
                            config.color
                          )}>
                            <p className="font-medium mb-1">🔴 {config.label}</p>
                            <p>{config.message}</p>
                          </div>
                        );
                      }

                      // No warning for safe countries
                      return null;
                    })()}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Security Measures Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              Security Measures
            </CardTitle>
            <CardDescription>
              Describe the security measures in place to protect personal data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <Label className="mb-3 block">
                Electronic Security Measures
                <span className="text-muted-foreground font-normal ml-1">(select all that apply)</span>
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {ELECTRONIC_SECURITY_MEASURES.map(measure => (
                  <div key={measure} className="flex items-center space-x-2">
                    <Checkbox
                      id={`esec-${measure}`}
                      checked={formData.electronic_security_measures?.includes(measure) || false}
                      onCheckedChange={() => toggleMultiSelect('electronic_security_measures', measure)}
                    />
                    <label htmlFor={`esec-${measure}`} className="text-sm cursor-pointer">
                      {measure}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <Label className="mb-3 block">
                Physical Security Measures
                <span className="text-muted-foreground font-normal ml-1">(select all that apply)</span>
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {PAPER_SECURITY_MEASURES.map(measure => (
                  <div key={measure} className="flex items-center space-x-2">
                    <Checkbox
                      id={`psec-${measure}`}
                      checked={formData.paper_security_measures?.includes(measure) || false}
                      onCheckedChange={() => toggleMultiSelect('paper_security_measures', measure)}
                    />
                    <label htmlFor={`psec-${measure}`} className="text-sm cursor-pointer">
                      {measure}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2 pt-4 border-t">
              <Label>Additional Security Notes</Label>
              <textarea
                value={formData.security_notes || ''}
                onChange={(e) => handleChange('security_notes', e.target.value)}
                placeholder="Add any additional security measures or details not listed above"
                className="w-full p-2 border rounded text-sm min-h-24 bg-white dark:bg-slate-900 text-black dark:text-white"
              />
            </div>
          </CardContent>
        </Card>

        {/* Inline Review Summary (collapsible) */}
        <Card>
          <CardHeader
            onClick={() => setSummaryExpanded(!summaryExpanded)}
            className="cursor-pointer hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5" />
                Review Before Submitting
              </CardTitle>
              <span className={cn(
                "transition-transform",
                summaryExpanded ? "rotate-180" : ""
              )}>
                ▼
              </span>
            </div>
          </CardHeader>
          {summaryExpanded && (
            <CardContent className="space-y-4 border-t pt-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm">
                <p className="text-blue-900 dark:text-blue-100">
                  ℹ️ This summary is read-only. Click on any step above to edit it, or scroll down to save your record.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 border rounded-lg">
                  <p className="text-xs text-muted-foreground">Processing Activity</p>
                  <p className="font-medium">{formData.process_name || '-'}</p>
                </div>
                <div className="p-3 border rounded-lg">
                  <p className="text-xs text-muted-foreground">Department</p>
                  <p className="font-medium">{formData.department || '-'}</p>
                </div>
              </div>

              <div className="p-3 border rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">Legal Basis</p>
                <p className="font-medium">{getLegalBasisLabel(formData.legal_basis) || '-'}</p>
              </div>

              <div className="grid grid-cols-3 gap-3 text-center text-sm">
                <div className="p-3 border rounded-lg">
                  <p className="text-xs text-muted-foreground">Retention</p>
                  <p className="font-medium">{formData.retention_period || '-'}</p>
                </div>
                <div className="p-3 border rounded-lg">
                  <Badge variant={formData.shares_internally ? "default" : "secondary"}>
                    {formData.shares_internally ? 'Internal: Yes' : 'Internal: No'}
                  </Badge>
                </div>
                <div className="p-3 border rounded-lg">
                  <Badge variant={formData.shares_externally ? "default" : "secondary"}>
                    {formData.shares_externally ? 'External: Yes' : 'External: No'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Status Selection */}
        <Card>
          <CardContent className="space-y-4 pt-6">
            {/* Status Selection - Role-Aware */}
            <div className="space-y-2">
              <Label>Record Status</Label>
              {(() => {
                const availableStatuses = getAvailableStatuses(user?.role);
                const STATUS_LABELS = {
                  draft: 'Draft',
                  pending_review: 'Pending Review',
                  approved: 'Approved',
                  active: 'Active',
                  archived: 'Archived'
                };

                return (
                  <Select
                    value={formData.status}
                    onValueChange={(val) => handleChange('status', val)}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableStatuses.map(status => (
                        <SelectItem key={status} value={status}>
                          {STATUS_LABELS[status]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              })()}
              {user?.role && ['client_view', 'entity_view'].includes(user.role) && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                  ℹ️ As staff, you can create and submit records for review. Only admins can approve and activate records.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };


  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1: return renderBasics();
      case 2: return renderDataCollected();
      case 3: return renderLegalBasis();
      case 4: return renderDataSources();
      case 5: return renderStorageSharingSecurityWithSummary();
      default: return renderBasics();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className={cn("min-h-screen bg-background", theme === 'dark' ? 'dark' : '')}>
      {/* Header */}
      <div className="border-b bg-card sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/compliance/ropa')}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
              <div>
                <h1 className="text-xl font-semibold">
                  {isEditing ? 'Edit Processing Activity' : 'Add Processing Activity'}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {formData.process_name || 'Record of Processing Activities (ROPA)'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Auto-save indicator */}
              {recordId && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  {autoSaveStatus === 'saving' ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Saving...
                    </>
                  ) : autoSaveStatus === 'saved' ? (
                    <>
                      <Check className="h-3 w-3 text-green-500" />
                      Auto-saved
                    </>
                  ) : null}
                </span>
              )}
              <Badge variant={formData.status === 'active' ? 'default' : 'secondary'}>
                {formData.status}
              </Badge>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content with Sidebar Stepper */}
      <div className="flex">
        {/* Left Sidebar - Stepper */}
        <div className="hidden lg:block w-72 border-r bg-muted/30 p-4 sticky top-[73px] h-[calc(100vh-73px)] overflow-y-auto">
          <div className="mb-6">
            <StepperProgress current={currentStep} total={STEPS.length} />
          </div>
          <FormStepper
            steps={STEPS.map(step => ({
              id: step.id,
              label: step.label,
              icon: step.icon
            }))}
            currentStep={currentStep}
            onStepClick={(step) => { setCurrentStep(step); setShowValidationAlert(false); }}
            completedSteps={completedSteps}
          />
        </div>

        {/* Mobile Tab Navigation */}
        <div className="lg:hidden border-b bg-muted/30 w-full fixed top-[73px] z-10">
          <ScrollArea className="w-full">
            <div className="flex gap-1 p-2">
              {STEPS.map((step) => (
                <Button
                  key={step.id}
                  variant={currentStep === step.id ? "default" : "ghost"}
                  size="sm"
                  className="whitespace-nowrap"
                  onClick={() => { setCurrentStep(step.id); setShowValidationAlert(false); }}
                >
                  <step.icon className="h-4 w-4 mr-2" />
                  {step.label}
                </Button>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 max-w-4xl lg:pt-6 pt-20">
          {/* Validation Alert */}
          {showValidationAlert && validationErrors.length > 0 && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Please fix the following:</strong>
                <ul className="list-disc list-inside mt-1">
                  {validationErrors.map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {React.createElement(STEPS[currentStep - 1].icon, { className: "h-5 w-5" })}
                {STEPS[currentStep - 1].label}
              </CardTitle>
              <CardDescription>
                Step {currentStep} of {STEPS.length}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {renderStepContent()}
            </CardContent>
          </Card>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-6">
            <Button variant="outline" onClick={goToPrevStep} disabled={currentStep === 1}>
              <ChevronLeft className="h-4 w-4 mr-2" /> Previous
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/compliance/ropa')}>
                Cancel
              </Button>
              {currentStep === STEPS.length ? (
                <Button onClick={handleSave} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Processing Activity'}
                </Button>
              ) : (
                <Button onClick={goToNextStep}>
                  Next <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
